// src/views/panelMantenimiento/Services/MaintenanceService.js

const CACHE_KEY = 'maintenance_status_cache_v1';
const CACHE_TTL_MS = 60 * 1000; // 60s (seguro para status time-based)
let inFlight = null;

function toMs(v) {
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

function normalizeStatus(raw) {
  if (!raw || typeof raw !== 'object') return raw;

  // soporta backend camelCase o snake_case
  const enabled = !!(raw.enabled ?? raw.is_enabled);
  const startsAt = raw.startsAt ?? raw.starts_at ?? null;
  const endsAt = raw.endsAt ?? raw.ends_at ?? null;
  const message = raw.message ?? null;

  // si backend ya manda active/upcoming, los recalculamos por tiempo si tenemos fechas
  let active = !!raw.active;
  let upcoming = !!raw.upcoming;

  if (enabled && startsAt && endsAt) {
    const now = Date.now();
    const s = toMs(startsAt);
    const e = toMs(endsAt);
    if (s != null && e != null) {
      active = now >= s && now <= e;
      upcoming = now < s;
    }
  } else if (!enabled) {
    active = false;
    upcoming = false;
  }

  return { enabled, active, upcoming, startsAt, endsAt, message };
}

function getIsProd() {
  // Vue CLI
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) {
    return process.env.NODE_ENV === 'production';
  }
  return false;
}

export const MaintenanceService = {
  async getStatus({ force = false } = {}) {
    const isProd = getIsProd();

    if (!force && isProd) {
      try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          const { data, fetchedAt } = JSON.parse(raw);
          if (data && fetchedAt && (Date.now() - fetchedAt) < CACHE_TTL_MS) {
            return normalizeStatus(data);
          }
        }
      } catch { /* noop */ }
    }

    // evita “doble fetch” si navegas rápido
    if (!force && inFlight) return inFlight;

    inFlight = (async () => {
      const res = await fetch('/api/maintenance/status', {
        headers: { 'Cache-Control': 'no-cache' },
        credentials: 'include'
      });
      if (!res.ok) throw new Error('No se pudo obtener el estado de mantenimiento');

      const data = await res.json();
      const normalized = normalizeStatus(data);

      if (isProd) {
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            data: normalized,
            fetchedAt: Date.now()
          }));
        } catch { /* noop */ }
      }

      return normalized;
    })();

    try {
      return await inFlight;
    } finally {
      inFlight = null;
    }
  },

  async getFull() {
    const res = await fetch('/api/maintenance', {
      headers: { 'Cache-Control': 'no-store' },
      credentials: 'include'
    });
    if (!res.ok) throw new Error('No se pudo obtener la programación');
    return res.json();
  },

  async schedule(payload) {
    const res = await fetch('/api/maintenance/schedule', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.error) throw new Error(data?.error || 'No se pudo programar');

    // invalidate cache
    try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
    return data;
  },

  invalidateCache() {
    try { localStorage.removeItem(CACHE_KEY); } catch { /* noop */ }
  }
};